import { useState, useEffect } from 'react'
import { useDashboardLayoutStore, type WidgetMeta } from '../stores/dashboardLayoutStore'
import { useMonthlyDashboardLayoutStore } from '../stores/monthlyDashboardLayoutStore'
import { useCustomRangeDashboardLayoutStore } from '../stores/customRangeDashboardLayoutStore'
import { useInventoryDashboardLayoutStore } from '../stores/inventoryDashboardLayoutStore'

const CATEGORY_ICONS: Record<string, string> = {
    KPI: '📊',
    Chart: '📈',
    Table: '📋',
}

const CATEGORIES = ['All', 'KPI', 'Chart', 'Table']

interface Props {
    onClose: () => void
    dashboardType?: 'daily' | 'monthly' | 'custom-range' | 'inventory'
}

export default function WidgetGallery({ onClose, dashboardType = 'daily' }: Props) {
    const dailyStore = useDashboardLayoutStore()
    const monthlyStore = useMonthlyDashboardLayoutStore()
    const customStore = useCustomRangeDashboardLayoutStore()
    const inventoryStore = useInventoryDashboardLayoutStore()

    const store = dashboardType === 'monthly' ? monthlyStore :
                  dashboardType === 'custom-range' ? customStore :
                  dashboardType === 'inventory' ? inventoryStore :
                  dailyStore

    const availableWidgets = store.availableWidgets as WidgetMeta[]
    const activeWidgets = store.activeWidgets
    const addWidget = store.addWidget

    const [category, setCategory] = useState('All')
    const [search, setSearch] = useState('')
    const [hideAdded, setHideAdded] = useState(false)

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    const filtered = availableWidgets.filter(w => {
        if (category !== 'All' && w.category !== category) return false
        if (hideAdded && activeWidgets.includes(w.id)) return false
        if (search) {
            const q = search.toLowerCase()
            return w.label.toLowerCase().includes(q)
                || w.description.toLowerCase().includes(q)
                || w.category.toLowerCase().includes(q)
        }
        return true
    })

    const handleAdd = (w: WidgetMeta) => {
        addWidget(w.id)
    }

    return (
        <div className="gallery-overlay">
            <div className="gallery-modal">
                {/* Header */}
                <div className="gallery-header">
                    <h2>➕ Widget Gallery</h2>
                    <button className="gallery-close-btn" onClick={onClose}>✕</button>
                </div>

                {/* Filters */}
                <div className="gallery-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                        <div className="gallery-category-pills">
                            {CATEGORIES.map(c => (
                                <button
                                    key={c}
                                    className={`gallery-pill${category === c ? ' active' : ''}`}
                                    onClick={() => setCategory(c)}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <label className="dashboard-toggle" onClick={() => setHideAdded(!hideAdded)} style={{ margin: 0, cursor: 'pointer' }}>
                            <div className={`toggle-track ${hideAdded ? 'on' : ''}`}>
                                <div className="toggle-thumb" />
                            </div>
                            <span className="dashboard-toggle-label" style={{ fontSize: '13px' }}>Hide added</span>
                        </label>
                    </div>
                    <input
                        className="gallery-search"
                        placeholder="Search widgets…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        autoFocus
                        style={{ flex: 1, minWidth: '200px' }}
                    />
                </div>

                {/* Grid */}
                <div className="gallery-body">
                    <div className="gallery-grid">
                        {filtered.length === 0 && (
                            <div className="gallery-empty">
                                No widgets match your search.
                            </div>
                        )}
                        {filtered.map(w => {
                            const isAdded = activeWidgets.includes(w.id)
                            return (
                                <div key={w.id} className={`gallery-card${isAdded ? ' added' : ''}`}>
                                    <div className={`gallery-card-preview ${w.category}`}>
                                        {CATEGORY_ICONS[w.category] ?? '📦'}
                                    </div>
                                    <div className="gallery-card-name">{w.label}</div>
                                    <div className="gallery-card-desc">{w.description}</div>
                                    <div className="gallery-card-footer">
                                        <span className="gallery-card-category">{w.category}</span>
                                        <button
                                            className="gallery-card-add"
                                            disabled={isAdded}
                                            onClick={() => handleAdd(w)}
                                        >
                                            {isAdded ? '✓ Added' : '+ Add'}
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="gallery-footer">
                    <span>Showing {filtered.length} of {availableWidgets.length} widgets</span>
                    <span>{activeWidgets.length} widgets on dashboard</span>
                </div>
            </div>
        </div>
    )
}
